/**
 * Web Storage access that never throws. Reading window.localStorage / window.sessionStorage can
 * throw outright (Safari private mode, storage disabled by policy, sandboxed iframes) and setItem
 * can throw on quota — none of which should break the calling feature. Every accessor degrades to
 * a null / no-op instead.
 *
 * Storage-agnostic: callers pick 'local' or 'session'. This is the single copy of the
 * graceful-degradation logic — lib/gtm/localStorage and lib/remedyDraft both delegate here.
 */

export type StorageKind = 'local' | 'session';

function resolve(kind: StorageKind): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

export function safeStorageGet(kind: StorageKind, key: string): string | null {
  const store = resolve(kind);
  if (!store) return null;
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

export function safeStorageSet(kind: StorageKind, key: string, value: string): void {
  const store = resolve(kind);
  if (!store) return;
  try {
    store.setItem(key, value);
  } catch {
    // quota exceeded / storage unavailable — non-fatal
  }
}

export function safeStorageRemove(kind: StorageKind, key: string): void {
  const store = resolve(kind);
  if (!store) return;
  try {
    store.removeItem(key);
  } catch {
    // storage unavailable — non-fatal
  }
}
